import cx from 'classnames';
import {find, isNumber} from 'lodash';
import {useCallback, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, IconName, NetworkQuality, notifier, Tooltip} from 'components';
import {BROWSER_SUPPORT_ERROR, NOT_ALLOWED_ERROR, NOT_OFFICIALLY_SUPPORTED} from 'constants/webrtc';
import {UserSessionContext} from 'context';
import {useUserSession} from 'hooks';
import {AnyParticipant, ClientDevice, SessionActivityAudioVideoData, SessionActivityName} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {ParticipantRole} from 'types';

import {DevicePermission, InSessionAction} from '../types';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.AUDIO_VIDEO_CONNECTION;
const ACTIVITY_NAMES = TRANSLATION_KEYS.FEATURES.SESSION.ACTIVITY;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;

type Props = {
    participant: AnyParticipant;
    className?: string;
}

export const AudioVideoConnectionStatus: React.FC<Props> = ({participant, className}: Props) => {
    const {t} = useTranslation();
    const {audioVideoActivity, audioActivity, canChangeParticipantDevicePermission} = useUserSession();
    const {participantsNetworkQuality, requestUpdateParticipantDevicePermission, inProgressActions} = useContext(UserSessionContext);
    const activity = useMemo(() => audioVideoActivity || audioActivity, [audioVideoActivity, audioActivity]);

    const toggleMediaDisabled = useMemo(() =>
        participant.role === ParticipantRole.Client &&
        participant.data?.device === ClientDevice.IosSDK &&
        !participant.data?.appearance?.webview,
    [participant]);

    const toggleParticipantDevicePermissionAction = useMemo(() => find(inProgressActions, ({action, data}) =>
        action === InSessionAction.ToggleParticipantDevicePermission &&
        data?.participantId === participant?.id), [inProgressActions, participant]);

    const inProgressDevicePermissionToggle = useMemo(() => toggleParticipantDevicePermissionAction?.data?.devicePermission,
        [toggleParticipantDevicePermissionAction]);

    const toggleAudioBtnDisabled = useMemo(() => toggleMediaDisabled || inProgressDevicePermissionToggle === DevicePermission.Audio,
        [inProgressDevicePermissionToggle, toggleMediaDisabled]);
    const toggleVideoBtnDisabled = useMemo(() => toggleMediaDisabled || inProgressDevicePermissionToggle === DevicePermission.Video,
        [inProgressDevicePermissionToggle, toggleMediaDisabled]);

    const {activityName, activityIcon}: {activityName?: string, activityIcon?: IconName} = useMemo(() => {
        if (!activity) {
            return {};
        }
        if (activity.name === SessionActivityName.AudioVideo) {
            return {
                activityName: ACTIVITY_NAMES.AUDIO_VIDEO,
                activityIcon: 'audio-video'
            };
        } else {
            return {
                activityName: ACTIVITY_NAMES.AUDIO,
                activityIcon: 'audio'
            };
        }
    }, [activity]);

    const participantData = useMemo(() => {
        const data = activity?.data as SessionActivityAudioVideoData;
        return data?.participants?.[participant.id];
    }, [activity?.data, participant]);

    const connection = useMemo(() => participantData?.connection, [participantData]);
    const videoDisabled = useMemo(() => Boolean(participantData?.devicePermission?.video?.disabled), [participantData]);
    const audioDisabled = useMemo(() => Boolean(participantData?.devicePermission?.audio?.disabled), [participantData]);

    const hasError = useMemo(() => connection?.status === 'error', [connection]);

    const hasWarning = useMemo(() => Boolean(connection?.code) &&
            connection?.status === 'success', [connection]);

    const hasNetworkQuality = useMemo(() =>
        isNumber(participantsNetworkQuality?.[participant.id]), [participantsNetworkQuality, participant]);

    const message = useMemo(() => {
        if (connection?.code === NOT_ALLOWED_ERROR) {
            return t(T_KEYS.NOT_ALLOWED_MESSAGE);
        }
        if (connection?.code === BROWSER_SUPPORT_ERROR) {
            return t(T_KEYS.NOT_SUPPORTED_MESSAGE);
        }
        if (connection?.code === NOT_OFFICIALLY_SUPPORTED) {
            return t(T_KEYS.NOT_OFFICIALLY_SUPPORTED_MESSAGE);
        }
        return connection?.message;
    }, [t, connection]);

    const toggleParticipantDevicePermission = useCallback((devicePermission: DevicePermission) => {
        try {
            const value = devicePermission === DevicePermission.Audio ? audioDisabled : videoDisabled;
            requestUpdateParticipantDevicePermission(participant.id, devicePermission, !value);
        } catch {
            notifier.error({title: t(T_KEYS_COMMON.MESSAGES.ERROR.UNEXPECTED)});
        }
    }, [requestUpdateParticipantDevicePermission, participant, audioDisabled, videoDisabled, t]);

    if (!activity) {
        return null;
    }

    return (
        <div className={cx('tw-flex tw-items-center tw-text-sm tw-text-default-dimmed', className)}>
            <Icon name={activityIcon} size='sm' className='tw-mr-1' />
            {t(activityName)}
            <div className='tw-flex-1' />
            {hasNetworkQuality && <NetworkQuality value={participantsNetworkQuality?.[participant.id]} />}
            <Tooltip header={message} position='top'>
                {hasError && <Icon name='alert-circle' size='sm' color='alert' />}
                {hasWarning && <Icon name='alert-triangle' size='sm' color='warning' />}
            </Tooltip>
            <div className='tw-flex tw-ml-2'>
                {activity.name === SessionActivityName.AudioVideo &&
                    <Icon
                        className='tw-mr-1'
                        disabled={!canChangeParticipantDevicePermission || toggleVideoBtnDisabled}
                        size='sm'
                        color={videoDisabled ? 'alert' : 'default'}
                        name={videoDisabled ? 'video-off' : 'video-on'}
                        button={canChangeParticipantDevicePermission}
                        onClick={() => toggleParticipantDevicePermission(DevicePermission.Video)}
                    />}
                <Icon
                    disabled={!canChangeParticipantDevicePermission || toggleAudioBtnDisabled}
                    size='sm'
                    color={audioDisabled ? 'alert' : 'default'}
                    name={audioDisabled ? 'microphone-off' : 'microphone-on'}
                    button={canChangeParticipantDevicePermission}
                    onClick={() => toggleParticipantDevicePermission(DevicePermission.Audio)}
                />
            </div>
        </div>);
};